/**
 * Lightweight "similar content" scoring — no ML, no recommendation service.
 * Ranks a pool of candidates by genre overlap with a reference item, then
 * recency, so release/artist pages can show a "You might also like" strip.
 *
 * Deliberately client-side-filtered over a bounded `limit(N)` scan rather than
 * a Firestore `where('genres', 'array-contains', ...)` query — this repo
 * avoids adding new composite indexes for exactly this kind of feature (see
 * the Community Wall postmortem in CLAUDE.md: an unindexed compound query
 * silently returns nothing rather than erroring).
 */

export function normGenreList(genres: unknown, fallbackGenre?: unknown): string[] {
  const list = Array.isArray(genres) && genres.length > 0
    ? genres
    : (typeof fallbackGenre === 'string' && fallbackGenre ? [fallbackGenre] : []);
  return list.map((g) => String(g).trim().toLowerCase()).filter(Boolean);
}

/** Number of shared genres between two lowercase genre lists. */
export function genreOverlap(a: string[], b: string[]): number {
  if (!a.length || !b.length) return 0;
  const setB = new Set(b);
  let n = 0;
  for (const g of a) if (setB.has(g)) n++;
  return n;
}

function toMillis(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

export interface SimilarCandidate {
  id: string;
  genres: string[];
  dateField?: any;
  [key: string]: any;
}

function plain(s: unknown): string {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function artistOf(x: any): string {
  return plain((typeof x?.artist === 'string' ? x.artist : x?.artist?.name) || x?.artistName);
}

/**
 * True when a candidate release IS what the page shows, or contains it — a
 * page never recommends itself (Andrés 2026-11-01: "this list should not
 * recommend the song you are already standing in"). On a track page the
 * track's own release was the best genre match, so it topped the list.
 * Matches by id, by the release's track list, or by the same title from the
 * same artist (a re-upload or a single of the same song).
 */
export function isSelfOrParent(
  candidate: { id: string; title?: unknown; tracks?: unknown; [key: string]: any },
  current: { ids: Array<string | null | undefined>; title?: unknown; [key: string]: any },
): boolean {
  const ids = new Set(current.ids.filter(Boolean) as string[]);
  if (ids.has(candidate.id)) return true;
  if (Array.isArray(candidate.tracks)) {
    for (const t of candidate.tracks) {
      if (t && (ids.has(t.workId) || ids.has(t.id))) return true;
    }
  }
  const title = plain(current.title);
  if (title && plain(candidate.title) === title) {
    const a = artistOf(current), b = artistOf(candidate);
    if (!a || !b || a === b) return true;
  }
  return false;
}

/**
 * Rank candidates by genre overlap (desc) then recency (desc); items with
 * zero genre overlap are only included if the pool of matches is thin, so the
 * section never renders empty just because genres weren't tagged.
 */
export function pickSimilar<T extends SimilarCandidate>(
  candidates: T[],
  referenceGenres: string[],
  excludeIds: Set<string>,
  count: number
): T[] {
  const pool = candidates.filter((c) => !excludeIds.has(c.id));
  const scored = pool.map((c) => ({
    item: c,
    score: genreOverlap(referenceGenres, c.genres),
    recency: toMillis(c.dateField),
  }));
  scored.sort((a, b) => (b.score - a.score) || (b.recency - a.recency));
  const withOverlap = scored.filter((s) => s.score > 0);
  const chosen = withOverlap.length >= count ? withOverlap : scored; // pad with recents if genre-thin
  return chosen.slice(0, count).map((s) => s.item);
}
